import { ArrowRightIcon } from '@eleveo/icons-react';
import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { DropdownItem, DropdownItemProps, Popover, PopoverProps } from 'reactstrap';

type Props = {
  className: string;
  dataTestId?: string;
  disabled?: boolean;
  items: DropdownItemProps[];
  offset?: string;
  placement?: PopoverProps['placement'];
} & React.PropsWithChildren;

export const DropdownSubmenu: React.FC<Props> = ({
  className,
  dataTestId,
  disabled = false,
  children,
  items,
  offset = '-2px,-8px',
  placement = 'left-start',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [isContainerAttached, setContainerAttached] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setContainerAttached(true);
  }, []);

  const close = () => setIsOpen(false); // Workaround for popper not disappearing after leaving submenu
  const toggle = () => setIsOpen((open) => !open);

  const onClickHandler = (e: React.SyntheticEvent<HTMLElement, MouseEvent>) => {
    if (e && e.nativeEvent.detail && e.currentTarget.contains(document.activeElement)) {
      // blur only when invoked by mouse click
      (document.activeElement as HTMLElement).blur();
    }
  };

  return (
    <div className="dropdown__submenu-container" ref={containerRef}>
      <DropdownItem
        className={classnames(className, {
          [`${className}--disabled`]: disabled,
        })}
        data-test-id={dataTestId}
        disabled={disabled}
        onClick={onClickHandler}
        toggle={false}
      >
        {children}
        <ArrowRightIcon
          className={classnames('ml-auto mr-0 text-xs text-blue-300', {
            'text-blue-700': isOpen,
          })}
        />
      </DropdownItem>

      {isContainerAttached && containerRef.current && !disabled && (
        <Popover
          container={containerRef.current}
          fade={false}
          hideArrow
          offset={offset}
          placement={placement}
          popperClassName="dropdown__submenu-popper"
          isOpen={isOpen}
          target={containerRef.current}
          toggle={toggle}
          trigger="hover"
        >
          <div className="dropdown__submenu" onMouseLeave={close}>
            {items.map((item) => (
              <DropdownItem {...item} key={item.key}>
                {item.children}
              </DropdownItem>
            ))}
          </div>
        </Popover>
      )}
    </div>
  );
};
